import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import Link from '../../util/RouterLink';
import { connect } from 'react-redux';
import { Box, Collapse, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import * as invoiceDateActions from '../../actions/invoiceDateActions';
import { logoutUser } from '../../actions/authenticateActions';
import { getPermissionByName, hasReportPermission } from '../../util/customMethods';
import { tokens, HEADING_FONT } from '../../theme';
import logo from '../../assets/img/logo-2-white.png';

// Report configuration - matches AVAILABLE_REPORTS in admin section
const AVAILABLE_REPORTS = [
    { type: 'ARS_USAGE', label: 'ARS usage', route: '/ars-usage-report' },
    { type: 'RESPONSE', label: 'Response times', route: '/response-report' },
    { type: 'RESPONSE_SPD', label: 'Response times', route: '/response-report-spd' }, // vendor only sees one
];

const itemSx = (active, depth = 0) => ({
    position: 'relative',
    mx: 1.5,
    mb: 0.25,
    pl: 2 + depth * 2.5,
    py: depth ? 0.75 : 1,
    borderRadius: 1.5,
    color: active ? '#fff' : tokens.sidebarText,
    bgcolor: active ? tokens.navyLight : 'transparent',
    '&::before': active
        ? { content: '""', position: 'absolute', left: -12, top: 8, bottom: 8, width: 4, borderRadius: 2, bgcolor: tokens.red }
        : {},
    '&:hover': { bgcolor: tokens.navyLight, color: '#fff' },
    '& .MuiListItemIcon-root': { color: 'inherit', minWidth: 36 },
});

const textProps = (depth = 0) => ({
    primaryTypographyProps: { fontSize: depth ? 13.5 : 14.5, fontWeight: depth ? 400 : 500 },
});

class LeftMenu extends Component {
    state = { reportsOpen: false, towSheetsOpen: false, expandedYear: null };

    componentDidMount() {
        const { authInfo } = this.props;
        if (!this.props.skipFetch && authInfo && authInfo.userId) this.props.getInvoiceMonthYears(authInfo.userId);
        const path = this.props.location.pathname;
        if (AVAILABLE_REPORTS.some((r) => r.route === path)) this.setState({ reportsOpen: true });
        if (path.startsWith('/home')) this.setState({ towSheetsOpen: true });
    }

    isActive = (route) => this.props.location.pathname === route;

    navigate = () => {
        if (this.props.onNavigate) this.props.onNavigate();
    };

    handleLogout = () => {
        const done = () => {
            localStorage.clear();
            this.props.history.push('/login');
        };
        this.props.dispatch(logoutUser(done, done));
    };

    openMonth = (year, monthNo) => {
        const { authInfo, history } = this.props;
        this.props.setInvoiceSelectedDate(parseInt(year, 10), parseInt(monthNo, 10), 0);
        this.props.getInvoiceList(authInfo.userId, parseInt(year, 10), parseInt(monthNo, 10));
        history.push('/home');
        this.navigate();
    };

    navLink = (route, label, Icon) => (
        <ListItemButton component={Link} to={route} onClick={this.navigate} sx={itemSx(this.isActive(route))}>
            <ListItemIcon>
                <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={label} {...textProps()} />
        </ListItemButton>
    );

    render() {
        const { invoiceMonthYears, invoiceSelectedDate, location } = this.props;
        const { reportsOpen, towSheetsOpen, expandedYear } = this.state;
        const enabledReports = AVAILABLE_REPORTS.filter((r) => hasReportPermission(r.type));
        const years = Object.keys(invoiceMonthYears || {}).sort((a, b) => b - a);
        const onTowSheets = location.pathname.startsWith('/home');

        return (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: tokens.navy, color: tokens.sidebarText }}>
                <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
                    <Box sx={{ bgcolor: '#fff', borderRadius: 1.5, py: 1, display: 'flex', justifyContent: 'center' }}>
                        <img src={logo} alt="All City Towing" style={{ height: 52 }} />
                    </Box>
                    <Box
                        sx={{ fontFamily: HEADING_FONT, fontSize: 13, letterSpacing: 1.2, color: '#8FA1BA', mt: 1.5, textAlign: 'center' }}
                    >
                        Vendor portal
                    </Box>
                </Box>

                <List component="nav" sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
                    {getPermissionByName('dashboard') && this.navLink('/dashboard', 'Dashboard', SpaceDashboardOutlinedIcon)}
                    {getPermissionByName('dashboard') && this.navLink('/newRequest', 'New request', AddCircleOutlineIcon)}
                    {getPermissionByName('map') && this.navLink('/map', 'Map', MapOutlinedIcon)}
                    {getPermissionByName('search') && this.navLink('/search', 'Invoice search', SearchIcon)}

                    {getPermissionByName('reports') && enabledReports.length > 0 && (
                        <>
                            <ListItemButton
                                onClick={() => this.setState({ reportsOpen: !reportsOpen })}
                                sx={itemSx(false)}
                                aria-expanded={reportsOpen}
                            >
                                <ListItemIcon>
                                    <AssessmentOutlinedIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary="Reports" {...textProps()} />
                                <ExpandMoreIcon
                                    fontSize="small"
                                    sx={{ transition: 'transform .2s', transform: reportsOpen ? 'rotate(180deg)' : 'none' }}
                                />
                            </ListItemButton>
                            <Collapse in={reportsOpen} unmountOnExit>
                                {enabledReports.map((report) => (
                                    <ListItemButton
                                        key={report.type}
                                        component={Link}
                                        to={report.route}
                                        onClick={this.navigate}
                                        sx={itemSx(this.isActive(report.route), 1)}
                                    >
                                        <ListItemText primary={report.label} {...textProps(1)} />
                                    </ListItemButton>
                                ))}
                            </Collapse>
                        </>
                    )}

                    {getPermissionByName('towSheets') && (
                        <>
                            <ListItemButton
                                onClick={() => this.setState({ towSheetsOpen: !towSheetsOpen })}
                                sx={itemSx(false)}
                                aria-expanded={towSheetsOpen}
                            >
                                <ListItemIcon>
                                    <DescriptionOutlinedIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary="Tow sheets" {...textProps()} />
                                <ExpandMoreIcon
                                    fontSize="small"
                                    sx={{ transition: 'transform .2s', transform: towSheetsOpen ? 'rotate(180deg)' : 'none' }}
                                />
                            </ListItemButton>
                            <Collapse in={towSheetsOpen} unmountOnExit>
                                {years.length === 0 && (
                                    <Box sx={{ pl: 7.5, py: 1, fontSize: 13, color: '#8FA1BA' }}>No tow sheets yet</Box>
                                )}
                                {years.map((year) => (
                                    <React.Fragment key={year}>
                                        <ListItemButton
                                            onClick={() => this.setState({ expandedYear: expandedYear === year ? null : year })}
                                            sx={itemSx(false, 1)}
                                            aria-expanded={expandedYear === year}
                                        >
                                            <ListItemText primary={year} {...textProps(1)} />
                                            <ExpandMoreIcon
                                                fontSize="small"
                                                sx={{ transition: 'transform .2s', transform: expandedYear === year ? 'rotate(180deg)' : 'none' }}
                                            />
                                        </ListItemButton>
                                        <Collapse in={expandedYear === year} unmountOnExit>
                                            {(invoiceMonthYears[year] || []).map((month) => {
                                                const active =
                                                    onTowSheets &&
                                                    invoiceSelectedDate &&
                                                    invoiceSelectedDate.selectedYear === parseInt(year, 10) &&
                                                    invoiceSelectedDate.selectedMonth === parseInt(month.monthNo, 10);
                                                return (
                                                    <ListItemButton
                                                        key={`${year}-${month.monthNo}`}
                                                        onClick={() => this.openMonth(year, month.monthNo)}
                                                        sx={itemSx(active, 2)}
                                                    >
                                                        <ListItemText primary={month.month} {...textProps(1)} />
                                                    </ListItemButton>
                                                );
                                            })}
                                        </Collapse>
                                    </React.Fragment>
                                ))}
                            </Collapse>
                        </>
                    )}
                </List>

                <Box sx={{ borderTop: `1px solid ${tokens.navyLight}`, py: 1 }}>
                    <ListItemButton onClick={this.handleLogout} sx={itemSx(false)}>
                        <ListItemIcon>
                            <LogoutIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Sign out" {...textProps()} />
                    </ListItemButton>
                </Box>
            </Box>
        );
    }
}

const mapStateToProps = (state) => ({
    invoiceSelectedDate: state.invoiceSelectedDate,
    authInfo: state.authInfo,
    invoiceMonthYears: state.invoiceMonthYears,
});

const mapDispatchToProps = (dispatch) => ({
    dispatch,
    setInvoiceSelectedDate: (year, month, date) => dispatch(invoiceDateActions.setInvoiceSelectedDate(year, month, date)),
    getInvoiceList: (user, year, month) => dispatch(invoiceDateActions.getInvoiceList(user, year, month)),
    getInvoiceMonthYears: (user) => dispatch(invoiceDateActions.getInvoiceMonthYears(user)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(LeftMenu));
